import AppleCerts from 'dashboard/Settings/AppleCerts.react';
import AppleTokens from 'dashboard/Settings/AppleTokens.react';
import AndroidCerts from 'dashboard/Settings/AndroidCerts.react';
import DashboardView from 'dashboard/DashboardView.react';
import Field from 'components/Field/Field.react';
import Fieldset from 'components/Fieldset/Fieldset.react';
import BoxFieldset from 'components/BoxFieldset/BoxFieldset.react';
import FlowView from 'components/FlowView/FlowView.react';
import FormButton from 'components/FormButton/FormButton.react';
import FormTable from 'components/FormTable/FormTable.react';
import getSiteDomain from 'lib/getSiteDomain';
import Label from 'components/Label/Label.react';
import React from 'react';
import renderFlowFooterChanges from 'lib/renderFlowFooterChanges';
import styles from 'dashboard/Settings/Settings.scss';
import TextInput from 'components/TextInput/TextInput.react';
import Toggle from 'components/Toggle/Toggle.react';
import Toolbar from 'components/Toolbar/Toolbar.react';
import unique from 'lib/unique';

export default class PushSettings extends DashboardView {
  constructor() {
    super();
    this.section = 'App Settings';
    this.subsection = 'Push';
  }

  legacyPushSettings() {
    window.open(`${getSiteDomain()}/products/apps/${this.context.currentApp.slug}/edit#push`, '_blank');
  }

  renderForm({
    changes, fields, setField, resetFields,
  }) {
    const pushSettingsFields =
      (<BoxFieldset el="settings__push_push_notification_settings">
        <div className={styles.settingsBox}>
          <Field
            className="oneliner"
            label={<Label
              className="oneliner"
              text="Enable client push?"
              description="Allow pushes to be sent using the public client keys. Useful during development, but we suggest disabling it on production apps." />}
            input={<Toggle
              onChange={setField.bind(this, 'client_push_enabled')}
              value={fields.client_push_enabled} />} />

          <div className={styles.hr} />

          <Field
            className="oneliner"
            label={<Label
              className="oneliner"
              text="Enable REST push?"
              description="Allow push notifications to be sent using the REST key. When enabled, be sure to keep your REST key secret." />}
            input={<Toggle
              onChange={setField.bind(this, 'rest_push_enabled')}
              value={fields.rest_push_enabled} />} />
        </div>
      </BoxFieldset>);

    const androidPushFields =
      (<BoxFieldset el="settings__push_android_push_credentials">
        <div className={styles.settingsBox}>
          {
            fields.gcm_credentials.length < 1
              ? <div>
                <Field
                  label={<Label
                    text="GCM/FCM Sender ID"
                    description='This is an integer listed under "Project Number" in the Google API console.' />}
                  input={<TextInput
                    value={fields.customGCMSenderID}
                    placeholder="GCM/FCM Sender ID"
                    onChange={setField.bind(this, 'customGCMSenderID')} />} />
                <Field
                  label={<Label
                    text="GCM API Key/ FCM Legacy Server Key"
                    description='This is listed under the "Authentication" section of the Google API console.' />}
                  input={<TextInput
                    value={fields.customGCMAPIKey}
                    placeholder="GCM API Key/ FCM Legacy Server Key"
                    onChange={setField.bind(this, 'customGCMAPIKey')} />} />
              </div>
              : null
          }
          {fields.gcm_credentials.length > 0 ? <Field
            label={<Label text="Existing Credentials" />}
            input={
              (<FormTable
                items={fields.gcm_credentials.map((credential, index) => {
                  return {
                    /* title: 'Credential #' + (index + 1).toString(), */
                    title: 'Credential',
                    onDelete: () => setField('gcm_credentials', []/* fields.gcm_credentials.filter(oldCred => !compareGCMCredentials(oldCred, credential) ) */),
                    notes: [
                      {
                        key: 'Sender ID',
                        value: credential.sender_id || '',
                      },
                      {
                        key: 'API Key',
                        value: credential.api_key || '',
                      },
                    ],
                  };
                })} />)} /> : null}
        </div>
      </BoxFieldset>);

    const windowsPushFields =
      (<Fieldset
        legend="Windows Push Credentials"
        description="Enable push for Windows apps.">
        <div className={styles.settingsBox}>
          <Field
            label={<Label
              text="Windows Keys" />}
            input={<FormButton onClick={this.legacyPushSettings.bind(this)} value="Configure" />} />
          {/* TODO(drewgross): make Push Credentials component
          <Field
            label={<Label text='Package Security Identifier (SID)' />}
            input={<TextInput
                  value={fields.windowsPackageSID}
                  placeholder='Package Security Identifier'
                  onChange={setField.bind(this, 'windowsPackageSID')} />} />
          <Field
            label={<Label text='Client Secret' />}
            input={<TextInput
                  value={fields.windowsClientSecret}
                  placeholder='Client Secret'
                  onChange={setField.bind(this, 'windowsClientSecret')} />} />
          */}
        </div>
      </Fieldset>);

    return (
      <div className={styles.settings_page}>
        <Toolbar section={this.section} subsection={this.subsection} />
        <div className={styles.holder}>
          {pushSettingsFields}
          <AppleCerts />
          <AppleTokens />
          {/* {androidPushFields} */}
          <AndroidCerts />
          {/* windowsPushFields */}
        </div>
      </div>
    );
  }

  renderContent() {
    if (!this.props.initialFields) {
      return <Toolbar section={this.section} subsection={this.subsection} />;
    }

    const initialFields = {
      client_push_enabled: this.props.initialFields.client_push_enabled,
      rest_push_enabled: this.props.initialFields.rest_push_enabled,
      gcm_credentials: this.props.initialFields.gcm_credentials || [],
      customGCMSenderID: '',
      customGCMAPIKey: '',
    };

    return (<FlowView
      initialFields={initialFields}
      footerContents={({ changes }) => renderFlowFooterChanges(changes, initialFields, pushFieldOptions)}
      onSubmit={({ changes, setField }) => {
        return this.props.saveChanges(changes).then(({ successes, failures }) => {
          for (const k in successes) {
            setField(k, successes[k]);
          }

          if (Object.keys(failures).length > 0) {
            return { error: Object.values(failures).join(' ') };
          }
        })
          .catch(({ error, failures = {} }) => {
            return { error: unique(Object.values(failures).concat(error)).join(' ') };
          });
      }}

      // afterSave={({ setField, changes }) => {
      //   setField('customGCMSenderID', '', true);
      //   setField('customGCMAPIKey', '', true);
      // }}

      validate={({ changes }) => {
        if (changes.customGCMAPIKey && !changes.customGCMSenderID || !changes.customGCMAPIKey && changes.customGCMSenderID) {
          return 'A GCM Sender ID and API Key are both required.';
        }
      }}
      renderForm={this.renderForm.bind(this)} />);
  }
}

const compareGCMCredentials = (c1, c2) => c1.sender_id === c2.sender_id;

let pushFieldOptions = {
  client_push_enabled: {
    friendlyName: 'client push',
    type: 'boolean',
  },
  rest_push_enabled: {
    friendlyName: 'REST push',
    type: 'boolean',
  },
  customGCMSenderID: {
    friendlyName: 'GCM Sender ID',
    type: 'addition',
  },
  customGCMAPIKey: {
    friendlyName: 'API key',
    type: 'addition',
  },
  gcmCredentials: {
    friendlyName: 'GCM Credential',
    friendlyNamePlural: 'GCM Credentials',
    type: 'set',
    equalityPredicate: compareGCMCredentials,
  },
  windowsPackageSID: {
    friendlyName: 'Windows package SID',
  },
  windowsClientSecret: {
    friendlyName: 'Windows client secret',
  },
};
